import { useEffect, useRef, RefObject } from "react";

import { SnapPoint } from "./snapPoints";

const VELOCITY_THRESHOLD_PX_PER_MS = 0.5;
const RUBBER_BAND_FACTOR = 0.15;
const VELOCITY_SAMPLE_WINDOW_MS = 100;
const DRAG_CLAIM_THRESHOLD_PX = 5;
/** Projection window for nearest-snap settle (ms). */
const VELOCITY_PROJECTION_MS = 180;

type VelocitySample = { y: number; time: number };

type UseSheetGesturesParams = {
  popupRef: RefObject<HTMLDivElement | null>;
  backdropRef: RefObject<HTMLDivElement | null>;
  open: boolean;
  dismissible: boolean;
  /** When the software keyboard is open, Body scroll wins — no sheet drag/dismiss. */
  keyboardOpen: boolean;
  /** When a nested sheet is open, this sheet does not claim drag. */
  hasNestedOpen: boolean;
  /** Sorted ascending translateY offsets for snap rests. Empty = content-height mode. */
  snapOffsets: number[];
  /** Snap point values aligned with `snapOffsets` (same order). */
  snapPointValues: SnapPoint[];
  /** Current resting translateY offset. */
  activeSnapOffset: number;
  /** Explicit panel height when using snapPoints; otherwise measured on release. */
  topSnapHeight: number | null;
  setDragging: (dragging: boolean) => void;
  onDismiss: () => void;
  onSnapPointChange: (snapPoint: SnapPoint) => void;
  /** Report dismiss progress (0–1) to a parent nested sheet while dragging down. */
  onNestedDrag?: (percentageDragged: number) => void;
  /** Report whether this sheet stayed open after a nested gesture release. */
  onNestedRelease?: (open: boolean) => void;
};

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function isFormField(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) {
    return false;
  }
  const tag = el.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
}

function getOverflowY(el: HTMLElement): string {
  return window.getComputedStyle(el).overflowY;
}

function isScrollable(el: HTMLElement): boolean {
  const overflowY = getOverflowY(el);
  return (
    (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") && el.scrollHeight > el.clientHeight
  );
}

function findScrollableAncestor(start: EventTarget | null, boundary: HTMLElement): HTMLElement | null {
  let node = start instanceof HTMLElement ? start : null;
  while (node && node !== boundary) {
    if (isScrollable(node)) {
      return node;
    }
    node = node.parentElement;
  }
  if (node === boundary && isScrollable(boundary)) {
    return boundary;
  }
  return null;
}

function canScrollInDirection(scrollable: HTMLElement, deltaY: number): boolean {
  if (deltaY > 0) {
    return scrollable.scrollTop > 0;
  }
  if (deltaY < 0) {
    return scrollable.scrollTop + scrollable.clientHeight < scrollable.scrollHeight - 1;
  }
  return false;
}

function computeVelocity(samples: VelocitySample[]): number {
  if (samples.length < 2) {
    return 0;
  }
  const latest = samples[samples.length - 1];
  const cutoff = latest.time - VELOCITY_SAMPLE_WINDOW_MS;
  let earliest = samples[0];
  for (let i = 0; i < samples.length; i += 1) {
    if (samples[i].time >= cutoff) {
      earliest = samples[i];
      break;
    }
  }
  const dt = latest.time - earliest.time;
  if (dt <= 0) {
    return 0;
  }
  return (latest.y - earliest.y) / dt;
}

function pushVelocitySample(samples: VelocitySample[], sample: VelocitySample) {
  samples.push(sample);
  const cutoff = sample.time - VELOCITY_SAMPLE_WINDOW_MS;
  while (samples.length > 2 && samples[0].time < cutoff) {
    samples.shift();
  }
}

function nearestRestingPosition(offset: number, restingPositions: number[]): number {
  let best = restingPositions[0];
  let bestDistance = Math.abs(offset - best);
  for (let i = 1; i < restingPositions.length; i += 1) {
    const candidate = restingPositions[i];
    const distance = Math.abs(offset - candidate);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * Project release point and settle to the nearest resting position.
 * Strong velocity biases one position in its direction.
 */
function resolveRestingPosition(offset: number, velocity: number, restingPositions: number[]): number {
  if (restingPositions.length === 0) {
    return offset;
  }

  if (restingPositions.length === 1) {
    return restingPositions[0];
  }

  const sorted = restingPositions.slice().sort((a, b) => a - b);

  if (Math.abs(velocity) > VELOCITY_THRESHOLD_PX_PER_MS) {
    const current = nearestRestingPosition(offset, sorted);
    const currentIndex = sorted.indexOf(current);
    if (velocity > 0) {
      return sorted[Math.min(currentIndex + 1, sorted.length - 1)];
    }
    return sorted[Math.max(currentIndex - 1, 0)];
  }

  const projected = offset + velocity * VELOCITY_PROJECTION_MS;
  return nearestRestingPosition(projected, sorted);
}

function applyTransform(el: HTMLElement | null, offsetY: number) {
  if (!el) {
    return;
  }
  el.style.transform = `translateY(${offsetY}px)`;
}

/** Current translateY in px (CSS enter animation or inline drag). */
function readTranslateY(el: HTMLElement): number {
  const transform = window.getComputedStyle(el).transform;
  if (!transform || transform === "none") {
    return 0;
  }
  if (transform.startsWith("matrix3d(")) {
    const parts = transform
      .slice(9, -1)
      .split(",")
      .map(part => Number.parseFloat(part.trim()));
    return Number.isFinite(parts[13]) ? parts[13] : 0;
  }
  if (transform.startsWith("matrix(")) {
    const parts = transform
      .slice(7, -1)
      .split(",")
      .map(part => Number.parseFloat(part.trim()));
    return Number.isFinite(parts[5]) ? parts[5] : 0;
  }
  return 0;
}

function stopCssTransition(el: HTMLElement | null) {
  if (!el) {
    return;
  }
  el.style.transition = "none";
}

function clearInlineTransition(el: HTMLElement | null) {
  if (!el) {
    return;
  }
  el.style.transition = "";
}

function applyBackdropOpacity(el: HTMLElement | null, progress: number) {
  if (!el) {
    return;
  }
  el.style.opacity = String(Math.max(0, Math.min(1, progress)));
}

function backdropOpacityForOffset(offset: number, lowestSnapOffset: number, closedPosition: number): number {
  if (offset <= lowestSnapOffset) {
    return 1;
  }
  const span = closedPosition - lowestSnapOffset;
  if (span <= 0) {
    return 0;
  }
  return 1 - Math.max(0, offset - lowestSnapOffset) / span;
}

function setSnapOffsetVar(el: HTMLElement | null, offsetY: number) {
  if (!el) {
    return;
  }
  el.style.setProperty("--ui-bottom-sheet-snap-offset", `${offsetY}px`);
}

/**
 * Pointer-driven drag with velocity, rubber-banding, snap settling, and scroll arbitration.
 * Per-frame transforms write to DOM refs, never via React state.
 * `data-dragging` is owned by React via context `dragging`.
 */
export function useSheetGestures({
  popupRef,
  backdropRef,
  open,
  dismissible,
  keyboardOpen,
  hasNestedOpen,
  snapOffsets,
  snapPointValues,
  activeSnapOffset,
  topSnapHeight,
  setDragging,
  onDismiss,
  onSnapPointChange,
  onNestedDrag,
  onNestedRelease,
}: UseSheetGesturesParams): void {
  const samplesRef = useRef<VelocitySample[]>([]);
  const startYRef = useRef(0);
  const offsetRef = useRef(0);
  const claimedRef = useRef(false);
  const decidingRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const startTargetRef = useRef<EventTarget | null>(null);
  const overscrollTargetRef = useRef<HTMLElement | null>(null);
  const onDismissRef = useRef(onDismiss);
  const onSnapPointChangeRef = useRef(onSnapPointChange);
  const onNestedDragRef = useRef(onNestedDrag);
  const onNestedReleaseRef = useRef(onNestedRelease);
  const activeSnapOffsetRef = useRef(activeSnapOffset);
  const snapOffsetsRef = useRef(snapOffsets);
  const snapPointValuesRef = useRef(snapPointValues);
  const topSnapHeightRef = useRef(topSnapHeight);
  const dismissibleRef = useRef(dismissible);

  onDismissRef.current = onDismiss;
  onSnapPointChangeRef.current = onSnapPointChange;
  onNestedDragRef.current = onNestedDrag;
  onNestedReleaseRef.current = onNestedRelease;
  activeSnapOffsetRef.current = activeSnapOffset;
  snapOffsetsRef.current = snapOffsets;
  snapPointValuesRef.current = snapPointValues;
  topSnapHeightRef.current = topSnapHeight;
  dismissibleRef.current = dismissible;

  const hasSnapPoints = snapOffsets.length > 0;
  const gesturesEnabled = open && !keyboardOpen && !hasNestedOpen && (dismissible || hasSnapPoints);
  const snapScrollLockEnabled = open && hasSnapPoints && !keyboardOpen;

  useEffect(() => {
    const node = popupRef.current;
    if (!node || !snapScrollLockEnabled) {
      return;
    }

    function onWheel(event: WheelEvent) {
      if (activeSnapOffsetRef.current !== 0) {
        event.preventDefault();
      }
    }

    node.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      node.removeEventListener("wheel", onWheel);
    };
  }, [snapScrollLockEnabled, popupRef]);

  useEffect(() => {
    const node = popupRef.current;
    if (!node || !gesturesEnabled) {
      return;
    }
    const popup: HTMLDivElement = node;

    function clearBackdropOpacity() {
      if (backdropRef.current) {
        backdropRef.current.style.opacity = "";
      }
    }

    function clearInlineTransform() {
      if (popupRef.current) {
        popupRef.current.style.transform = "";
      }
    }

    function settleToOffset(target: number) {
      clearInlineTransition(popupRef.current);
      clearInlineTransition(backdropRef.current);
      setSnapOffsetVar(popupRef.current, target);
      setDragging(false);

      if (prefersReducedMotion()) {
        clearInlineTransform();
        clearBackdropOpacity();
        return;
      }

      // Wait a frame so React can drop `data-dragging` (re-enable transitions)
      // before clearing the inline drag transform toward the CSS variable.
      requestAnimationFrame(() => {
        clearInlineTransform();
        clearBackdropOpacity();
      });
    }

    function setOverscrollContain(el: HTMLElement | null, contain: boolean) {
      if (!el) {
        return;
      }
      el.style.overscrollBehavior = contain ? "contain" : "";
    }

    function clearOverscroll() {
      setOverscrollContain(overscrollTargetRef.current, false);
      overscrollTargetRef.current = null;
    }

    function onPointerDown(event: PointerEvent) {
      if (event.button !== 0) {
        return;
      }
      if (isFormField(event.target)) {
        return;
      }

      pointerIdRef.current = event.pointerId;
      startYRef.current = event.clientY;
      offsetRef.current = activeSnapOffsetRef.current;
      claimedRef.current = false;
      decidingRef.current = true;
      startTargetRef.current = event.target;
      samplesRef.current = [{ y: event.clientY, time: event.timeStamp }];
    }

    function onPointerMove(event: PointerEvent) {
      if (pointerIdRef.current !== event.pointerId || (!decidingRef.current && !claimedRef.current)) {
        return;
      }

      const deltaY = event.clientY - startYRef.current;
      pushVelocitySample(samplesRef.current, { y: event.clientY, time: event.timeStamp });

      if (!claimedRef.current) {
        if (Math.abs(deltaY) < DRAG_CLAIM_THRESHOLD_PX) {
          return;
        }

        const atTopSnap = activeSnapOffsetRef.current === 0;
        const scrollable = findScrollableAncestor(startTargetRef.current, popup);

        // Below the top snap, the sheet claims every vertical drag.
        if (atTopSnap && scrollable && canScrollInDirection(scrollable, deltaY)) {
          decidingRef.current = false;
          pointerIdRef.current = null;
          return;
        }

        claimedRef.current = true;
        decidingRef.current = false;
        overscrollTargetRef.current = scrollable;
        setOverscrollContain(scrollable, true);

        // Interrupt CSS enter/settle immediately. Waiting for React `data-dragging`
        // leaves transform transition fighting inline drag writes (frame lag).
        const visualY = readTranslateY(popup);
        const restY = activeSnapOffsetRef.current;
        stopCssTransition(popup);
        stopCssTransition(backdropRef.current);
        startYRef.current = event.clientY - (visualY - restY);
        offsetRef.current = visualY;
        applyTransform(popup, visualY);
        setDragging(true);
        popup.setPointerCapture(event.pointerId);
      }

      event.preventDefault();

      const snapMode = snapOffsetsRef.current.length > 0;
      const measuredHeight = popup.getBoundingClientRect().height || 1;
      const panelHeight = topSnapHeightRef.current ?? measuredHeight;
      const lowestSnapOffset = snapMode ? Math.max(...snapOffsetsRef.current) : 0;
      const floor = dismissibleRef.current ? Number.POSITIVE_INFINITY : lowestSnapOffset;

      let offset = activeSnapOffsetRef.current + deltaY;

      if (offset < 0) {
        offset = offset * RUBBER_BAND_FACTOR;
      } else if (offset > floor) {
        const overshoot = offset - floor;
        offset = floor + overshoot * RUBBER_BAND_FACTOR;
      }

      offsetRef.current = offset;

      applyTransform(popup, offset);
      applyBackdropOpacity(backdropRef.current, backdropOpacityForOffset(offset, lowestSnapOffset, panelHeight));

      const rest = activeSnapOffsetRef.current;
      const dismissSpan = Math.max(1, panelHeight - rest);
      const nestedProgress = Math.max(0, Math.min(1, (offset - rest) / dismissSpan));
      onNestedDragRef.current?.(nestedProgress);
    }

    function onPointerUp(event: PointerEvent) {
      if (pointerIdRef.current !== event.pointerId) {
        return;
      }

      const wasClaimed = claimedRef.current;
      pointerIdRef.current = null;
      decidingRef.current = false;
      claimedRef.current = false;

      if (!wasClaimed) {
        return;
      }

      popup.releasePointerCapture(event.pointerId);
      clearOverscroll();

      const offset = offsetRef.current;
      const velocity = computeVelocity(samplesRef.current);
      const measuredHeight = popup.getBoundingClientRect().height || 1;
      const panelHeight = topSnapHeightRef.current ?? measuredHeight;
      const snapMode = snapOffsetsRef.current.length > 0;
      const offsets = snapOffsetsRef.current;
      const points = snapPointValuesRef.current;
      const closedPosition = panelHeight;

      const restingPositions = dismissibleRef.current
        ? snapMode
          ? [...offsets, closedPosition]
          : [0, closedPosition]
        : snapMode
        ? [...offsets]
        : [0];

      const target = resolveRestingPosition(offset, velocity, restingPositions);

      if (target === closedPosition && dismissibleRef.current) {
        clearInlineTransition(popup);
        clearInlineTransition(backdropRef.current);
        setDragging(false);
        onNestedReleaseRef.current?.(false);
        if (prefersReducedMotion()) {
          clearInlineTransform();
          clearBackdropOpacity();
          onDismissRef.current();
          return;
        }
        // Keep the current inline offset until `open` flips off, then Popup
        // clears inline styles so the CSS close transition runs from here.
        onDismissRef.current();
        return;
      }

      onNestedReleaseRef.current?.(true);

      if (snapMode) {
        const snapIndex = offsets.indexOf(target);
        if (snapIndex >= 0) {
          onSnapPointChangeRef.current(points[snapIndex]);
        }
      }

      settleToOffset(target);
    }

    function onPointerCancel(event: PointerEvent) {
      if (pointerIdRef.current !== event.pointerId) {
        return;
      }
      pointerIdRef.current = null;
      decidingRef.current = false;
      if (claimedRef.current) {
        claimedRef.current = false;
        clearOverscroll();
        onNestedReleaseRef.current?.(true);
        settleToOffset(activeSnapOffsetRef.current);
      }
    }

    popup.addEventListener("pointerdown", onPointerDown);
    popup.addEventListener("pointermove", onPointerMove, { passive: false });
    popup.addEventListener("pointerup", onPointerUp);
    popup.addEventListener("pointercancel", onPointerCancel);

    return () => {
      popup.removeEventListener("pointerdown", onPointerDown);
      popup.removeEventListener("pointermove", onPointerMove);
      popup.removeEventListener("pointerup", onPointerUp);
      popup.removeEventListener("pointercancel", onPointerCancel);
      clearOverscroll();
    };
  }, [gesturesEnabled, popupRef, backdropRef, setDragging]);
}
